// Simple scraping service for on-demand competitor data collection
// apiService import removed as unused

// Scraping API URL Constants
const SCRAPING_API_URLS = {
  local: 'http://localhost:3003',
  remote: 'https://your-scraping-api-production-url.com' // Update with actual production URL
} as const;

// Get Scraping API URL from environment or fall back to local
const getScrapingApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_SCRAPING_API_URL;
  const mode = import.meta.env.MODE;

  if (envUrl) {
    return envUrl;
  }

  // Fallback based on mode
  if (mode === 'remote') {
    return SCRAPING_API_URLS.remote;
  }

  return SCRAPING_API_URLS.local;
};

const SCRAPING_API_BASE_URL = getScrapingApiUrl();

export interface ScrapeRequest {
  customer_id: string;
  customer_account_id: string;
  platform_id: 1 | 2; // 1=Instagram, 2=TikTok
  platform_username: string;
}

export interface ScrapeResponse {
  success: boolean;
  operation_id?: number;
  message: string;
  error?: string;
}

export interface OperationStatus {
  operation_id: number;
  status: 'started' | 'completed' | 'failed';
  operation_type: string;
  started_at: string;
  completed_at?: string;
  execution_time_ms?: number;
  records_processed?: number;
  records_created?: number;
  records_updated?: number;
  error_message?: string;
  job_detail: {
    customer_account_id: string;
    platform_id: number;
    platform_username: string;
    scraping_type: string;
    job_ids?: number[];
  };
}

export interface OperationStatusResponse {
  success: boolean;
  data?: OperationStatus;
  message?: string;
  error?: string;
}

export interface CustomerOperation {
  id: number;
  customer_id: string;
  operation_type_id: number;
  operation_name: string;
  operation_status_id: number;
  status_name: string;
  operation_status: string;
  job_detail: {
    customer_account_id: string;
    platform_id: number;
    platform_username: string;
  };
  records_processed?: number;
  records_created?: number;
  records_updated?: number;
  error_message?: string;
  execution_time_ms?: number;
  started_at: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerOperationsResponse {
  success: boolean;
  data?: CustomerOperation[];
  pagination?: {
    limit: number;
    offset: number;
    total: number;
  };
  message?: string;
  error?: string;
}

class ScrapingService {
  private baseUrl = `${SCRAPING_API_BASE_URL}/scraping`;

  // Helper to get headers with auth token
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const authToken = localStorage.getItem('auth-token');
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    return headers;
  }

  // Request on-demand scraping for a competitor account
  async requestScraping(request: ScrapeRequest): Promise<ScrapeResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/ondemand`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(request),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data?.message || `HTTP ${response.status}: ${response.statusText}`,
          error: data?.error || 'REQUEST_ERROR'
        };
      }

      return data;
    } catch (error: any) {
      console.error('Scraping request failed:', error);
      return {
        success: false,
        message: error?.message || 'Failed to request scraping',
        error: 'NETWORK_ERROR'
      };
    }
  }

  // Get the status of a scraping operation
  async getOperationStatus(operationId: number): Promise<OperationStatusResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/status/${operationId}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Get operation status failed:', error);
      return {
        success: false,
        message: error?.message || 'Failed to get operation status',
        error: 'NETWORK_ERROR'
      };
    }
  }

  // Helper to get platform ID from name
  getPlatformId(platformName: 'instagram' | 'tiktok'): 1 | 2 {
    return platformName === 'instagram' ? 1 : 2;
  }

  // Get customer's scraping operations history
  async getCustomerOperations(
    customerId: string,
    options: { limit?: number; offset?: number } = {}
  ): Promise<CustomerOperationsResponse> {
    try {
      const { limit = 10, offset = 0 } = options;
      const url = new URL(`${this.baseUrl}/operations/${customerId}`);
      url.searchParams.append('limit', limit.toString());
      url.searchParams.append('offset', offset.toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Get customer operations failed:', error);
      return {
        success: false,
        message: error?.message || 'Failed to get customer operations',
        error: 'NETWORK_ERROR'
      };
    }
  }

  // Helper to get platform name from ID
  getPlatformName(platformId: 1 | 2): 'instagram' | 'tiktok' {
    return platformId === 1 ? 'instagram' : 'tiktok';
  }

  // Get portfolio analysis
  async getPortfolioAnalysis(payload: {
    customerId: string;
    startDate: string;
    endDate: string;
    platformIds: number[];
    accountIds?: string[];
  }): Promise<any> {
    try {
      const response = await fetch(`${SCRAPING_API_BASE_URL}/portfolio/analyze`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // Try to parse error response body for more details
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // If parsing fails, use the default error message
        }

        const error: any = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Portfolio analysis request failed:', error);
      throw error;
    }
  }
}

export const scrapingService = new ScrapingService();
export default scrapingService;